import { lazy } from "react"
import { graphql, useFragment } from "react-relay"
import { X } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { DrawerHeader } from "@/components/ui/drawer"
import { PatientInfoForWaitingRoom } from "@/modules/patients/components/patient-info/patient-info-for-waiting-room"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { appointmentDrawerPatient_WaitingRoomAppointment$data } from "@/modules/waiting-room/components/__generated__/appointmentDrawerPatient_WaitingRoomAppointment.graphql"
import { appointmentDrawerHeader_appointment$key } from "@/modules/waiting-room/components/appointment-drawer/__generated__/appointmentDrawerHeader_appointment.graphql"
import { appointmentDrawerHeader_patient$key } from "@/modules/waiting-room/components/appointment-drawer/__generated__/appointmentDrawerHeader_patient.graphql"
import { SofiaEnvironment } from "@/sofia-environment"

const AddChat = lazy(() =>
  import("@/modules/waiting-room/components/add-chat").then((module) => ({ default: module.AddChat })),
)

const AppointmentDrawerHeaderPatientFragment = graphql`
  fragment appointmentDrawerHeader_patient on Patient {
    id
    patientId
  }
`

const AppointmentDrawerHeaderFragment = graphql`
  fragment appointmentDrawerHeader_appointment on Appointment {
    language
    reason
    patientAddress {
      ...patientAddressTooltip_Address
    }
  }
`

type AppointmentDrawerHeader = {
  isPreview?: boolean
  appointmentRef: appointmentDrawerHeader_appointment$key
  appointmentPatientData: appointmentDrawerPatient_WaitingRoomAppointment$data["appointment"]["patient"]
  onClose: (() => void) | undefined
}

const AppointmentDrawerHeader = ({
  isPreview,
  appointmentRef,
  appointmentPatientData,
  onClose,
}: AppointmentDrawerHeader) => {
  const { userName } = useUser()
  const { language, reason, patientAddress } = useFragment<appointmentDrawerHeader_appointment$key>(
    AppointmentDrawerHeaderFragment,
    appointmentRef,
  )

  const appointmentDrawerHeaderPatient = useFragment<appointmentDrawerHeader_patient$key>(
    AppointmentDrawerHeaderPatientFragment,
    appointmentPatientData,
  )

  const patient = useFragment<PatientFragment$key>(PatientFragment, appointmentPatientData)

  const { patientId } = appointmentDrawerHeaderPatient

  return (
    <DrawerHeader
      className="animate-fade-in-bottom flex min-h-20 flex-row items-center gap-4 pt-1 pb-2"
      data-testid="appointment-drawer-head">
      <PatientInfoForWaitingRoom
        className="flex-wrap whitespace-normal"
        appointmentAddress={patientAddress}
        patient={patient}
        contactChannel={patient.contactChannel}
        patientId={patientId}>
        {SofiaEnvironment.AllowDebugFeatures && (
          <AddChat
            patientId={appointmentDrawerHeaderPatient.id}
            language={language}
            reason={reason}
            assignedTo={userName}
          />
        )}
      </PatientInfoForWaitingRoom>
      {!isPreview && (
        <Button
          data-testid="close-appointment-drawer-icon"
          onClick={onClose}
          variant="circle"
          className="h-10 w-10 shrink-0 p-2">
          <X />
        </Button>
      )}
    </DrawerHeader>
  )
}

export { AppointmentDrawerHeader }
